import React, { useState } from "react";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaPaperPlane,
} from "react-icons/fa";
import { FiLoader, FiCheckCircle } from "react-icons/fi";
import { assets } from "../../Assets/assets";
import axios from "../../Utils/axiosInstance";
import CommonButton from "../../Components/Common/CommonButton";
import { useNavigate } from "react-router-dom";

const OFFICE_ADDRESS =
  "Block-A, Bharati Tower, Forestpark, Ekamra Marg, Bhimpur, Bhubaneswar, Khordha, Odisha(India) - 751009";

const MAP_EMBED_SRC = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3743.073204969015!2d85.82131207456517!3d20.255798513879025!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a19a73170daaaab%3A0x588d0e58bf1e32e1!2sP.C.M%20Exports!5e0!3m2!1sen!2sin!4v1784632500092!5m2!1sen!2sin`;
const CONTACT_CARDS = [
  {
    icon: FaPhoneAlt,
    label: "Call Us",
    value: "+91 6752455372",
    href: "tel:+916752455372",
  },
  {
    icon: FaEnvelope,
    label: "Email Us",
    value: "pcmexportsbbsr@gmail.com",
    href: "mailto:pcmexportsbbsr@gmail.com",
  },
  {
    icon: FaWhatsapp,
    label: "WhatsApp",
    value: "+91 9776908224",
    href: "https://wa.me/919776908224",
  },
  {
    icon: FaClock,
    label: "Working Hours",
    value: "Mon - Sun, 9:00 AM - 6:00 PM",
    href: null,
  },
];

const REASON_CARDS = [
  {
    title: "Ask for a Quotation",
    desc: "Tell us what you need and get the lowest verified prices in India, fast.",
  },
  {
    title: "Become a Supplier",
    desc: "Onboard your business and reach verified B2B buyers across India.",
  },
  {
    title: "Partnership & Bulk Orders",
    desc: "Looking for a long-term trade partnership? Let's talk directly.",
  },
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

const ContactUs = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // "success" | "error" | null
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    try {
      setSubmitting(true);
      setStatus(null);
      await axios.post("/contact-us", form);
      setStatus("success");
      setForm(initialForm);
    } catch (err) {
      console.error("Contact form submission failed", err);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F5F5F5]">
      {/* HERO */}
      <div
        className="relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${assets.footerbanner})` }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 2xl:max-w-7xl mx-auto px-4 py-20 md:py-28 text-center">
          <p className="text-subPrimaryColor text-xs md:text-sm font-semibold tracking-[0.2em] uppercase mb-3">
            We'd Love To Hear From You
          </p>
          <h1 className="text-3xl md:text-5xl font-semibold text-white mb-4">
            Get In Touch With Us
          </h1>
          <p className="text-gray-300 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            Whether it's a quotation, a partnership, or just a question about
            QQT process flow &amp;, our team is ready to help.
          </p>
        </div>
      </div>

      {/* CONTACT INFO CARDS */}
      <div className="2xl:max-w-7xl mx-auto px-4 -mt-10 md:-mt-12 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CONTACT_CARDS.map(({ icon: Icon, label, value, href }) => {
            const CardInner = (
              <div className="h-full bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-5 flex flex-col items-center text-center gap-3 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                <div className="w-11 h-11 rounded-xl bg-primaryColor/10 flex items-center justify-center text-primaryColor">
                  <Icon size={16} />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-secondaryTextColor">
                    {label}
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-gray-800 mt-1 break-words">
                    {value}
                  </p>
                </div>
              </div>
            );
            return href ? (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
              >
                {CardInner}
              </a>
            ) : (
              <div key={label}>{CardInner}</div>
            );
          })}
        </div>
      </div>

      {/* FORM + MAP */}
      <div className="2xl:max-w-7xl mx-auto px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* FORM */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-1">
              Send Us A Message
            </h2>
            <p className="text-secondaryTextColor text-xs md:text-sm mb-6">
              Fill in the details below and our team will get back to you
              within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Full Name*
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Email Address*
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="you@company.com"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 00000 00000"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="Quotation, Partnership, Support..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Message*
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us a bit about what you need..."
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor transition resize-none"
                />
              </div>

              <div className="flex items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-6 py-2.5 bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white rounded-xl text-sm font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <FiLoader className="animate-spin" size={14} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane size={12} />
                      Send Message
                    </>
                  )}
                </button>

                {status === "success" && (
                  <span className="flex items-center gap-1.5 text-green-600 text-xs font-medium">
                    <FiCheckCircle size={14} />
                    Message sent — we'll be in touch soon!
                  </span>
                )}
                {status === "error" && (
                  <span className="text-red-500 text-xs font-medium">
                    Something went wrong. Please try again.
                  </span>
                )}
              </div>
            </form>
          </div>

          {/* MAP + ADDRESS */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm h-64 lg:h-[calc(100%-6.5rem)]">
              <iframe
                title="PCM Exports Office Location"
                src={MAP_EMBED_SRC}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="bg-primaryColor rounded-2xl p-5 text-white flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <FaMapMarkerAlt size={14} />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60 mb-1">
                  Our Office
                </p>
                <p className="text-sm leading-relaxed text-white/90">
                  {OFFICE_ADDRESS}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WHY REACH OUT */}
      <div className="bg-white py-16 md:py-20">
        <div className="2xl:max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">
              How Can We Help?
            </h2>
            <p className="text-secondaryTextColor text-sm max-w-lg mx-auto">
              Pick what fits and we'll route your message to the right team.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {REASON_CARDS.map((card) => (
              <div
                key={card.title}
                className="group bg-[#F5F5F5] hover:bg-primaryColor rounded-2xl p-6 border border-gray-200 hover:border-primaryColor transition-all duration-200"
              >
                <h3 className="text-base font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors duration-200">
                  {card.title}
                </h3>
                <p className="text-sm text-secondaryTextColor group-hover:text-white/80 leading-relaxed transition-colors duration-200">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA STRIP */}
      <div className="2xl:max-w-7xl mx-auto px-4 py-14 text-center">
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4">
          Prefer a quick quotation instead?
        </h2>
        <CommonButton
          onClick={() => navigate("/post-requirement")}
          label="Ask for a Quick Quotation"
        />
      </div>
    </div>
  );
};

export default ContactUs;